import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  Loader2,
  Lock,
  RefreshCw,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import {
  BillingMemberCard,
  BillingMemberRow,
  creditLeftAfterEnd,
  formatCycleEnd,
  hasEnded,
  hasLiveSubscription,
  type BillingSeat,
} from '@/components/molecules/org-admin/BillingMemberRow';
import { memberAllowance } from '@/services/org-admin/allowance';
import { billingErrorMessage, isAmbiguousFailure } from '@/components/molecules/org-admin/billingErrors';
import {
  cardBrandLabel,
  formatCardExpiry,
  isCardExpired,
} from '@/components/molecules/org-admin/paymentMethod';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import {
  OA_NARROW_BREAKPOINT,
  PlainHeader,
  useContainerWidth,
} from '@/components/molecules/org-admin/OrgAdminTable';
import {
  SETTLING_TARGETS,
  billingSignature,
  creditsSignature,
  reconcileSettling,
  usageSignature,
  type SettlingReason,
  type SettlingSnapshot,
  type SettlingTarget,
} from '@/components/molecules/org-admin/billingSettling';
import { PlanMatrix } from '@/components/molecules/org-admin/PlanMatrix';
import { creditPurchaseBlockedReason } from '@/components/molecules/org-admin/creditEligibility';
import { useIsEnterpriseConnection } from '@/hooks/auth/useCapabilities';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import { PlanPurchaseDialog } from '@/components/molecules/org-admin/PlanPurchaseDialog';
import { BulkTopUpDialog } from '@/components/molecules/org-admin/BulkTopUpDialog';
import { BuyCreditsDialog } from '@/components/molecules/org-admin/BuyCreditsDialog';
import { CreditLedgerTable } from './CreditLedgerTable';
import { InvoicesPanel } from './InvoicesPanel';
import { formatCurrencyUsd } from './usageFormat';
import { NO_PERMISSION_REASON, type OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useOrgMembers, useOrgUsageReport } from '@/hooks/org-admin';
import {
  useBillingPortal,
  useAssignCredits,
  useCancelSubscription,
  useChangePlan,
  useCreateCheckout,
  useOrgBilling,
  useOrgCredits,
  usePurchaseCredits,
  useResumeSubscription,
} from '@/hooks/org-admin/useBilling';
import { captureOrgToken, isOrgTokenCurrent } from '@/lib/org-scope';
import { cn } from '@/lib/utils';
import { plansAtCapacity, tallySeatPlans } from '@/services/org-admin';
import type { PurchasablePlan } from '@/services/org-admin';
import { OrgUsageStatRail, type StatSpec } from './OrgUsageStatRail';

/**
 * Billing.
 *
 * Placed before Usage in the tab bar: this is where money enters (plans, budgets, cards), Usage and
 * Analysis are where it is consumed, Rewards is where it is offset. Left to right, that is the money
 * story in causal order.
 *
 * The hard part of this screen is not the layout, it is that **Stripe is asynchronous**. Checkout
 * returns a URL, not a purchase; a top-up with a card on file returns "processing", not a budget. The
 * plan and budget only change when Stripe's webhook reaches our backend, which happens after the
 * browser has already been redirected back. So nothing here reports success on the strength of a
 * redirect or a 200 — every path ends in polling `GET /billing` and showing what it actually says.
 *
 * No store. Server state is React Query; which dialog is open and whether a purchase is settling are
 * facts about this page view, so they are component state.
 */

export interface BillingPanelProps {
  self: OrgAdminSelfContext;
}

/** A seat-level problem summarised above the tables: payment failures, ending and ended plans. */
function BillingNotice({
  tone,
  icon: Icon,
  label,
  title,
  children,
  action,
}: {
  tone: 'warn' | 'bad';
  icon: LucideIcon;
  label: string;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const bad = tone === 'bad';
  return (
    <section
      role="status"
      aria-label={label}
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 rounded-[var(--oa-radius-md)] border p-3',
        bad ? 'border-destructive/25 bg-destructive/[0.06]' : 'border-warning/25 bg-warning/[0.06]',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-[13px] font-semibold">
          <Icon
            className={cn('h-4 w-4 shrink-0', bad ? 'text-destructive-strong' : 'text-warning-strong')}
            aria-hidden
          />
          {title}
        </p>
        <p className="oa-meta mt-1 pl-6">{children}</p>
      </div>
      {action}
    </section>
  );
}

/** "a@x.io on Oct 3, b@x.io on Oct 12" — each seat with the date that matters for it. */
function seatDates(seats: BillingSeat[], dateOf: (seat: BillingSeat) => string | null): string {
  return seats
    .map((seat) => {
      const iso = dateOf(seat);
      const date = iso ? formatCycleEnd(iso)?.title : null;
      return date ? `${seat.email} on ${date}` : seat.email;
    })
    .join(', ');
}

/** How long to poll after a Stripe operation before telling the admin to check back. */
const POLL_WINDOW_MS = 60_000;
const POLL_INTERVAL_MS = 3_000;

/**
 * `plan` carries a `seat` when it was opened from that member's row, and `null` when it was opened
 * from the plan catalogue — where a plan has been chosen but a member has not. The dialog renders a
 * member picker for the second case and behaves exactly as before for the first.
 */
type DialogState =
  | { kind: 'plan'; seat: BillingSeat | null; plan?: PurchasablePlan }
  // Buy into the org pool (no seat), and spend the pool onto seats. Two dialogs because they are
  // two decisions — which is the entire reason the pool exists rather than a bulk charge.
  | { kind: 'buy-credits' }
  | { kind: 'assign-credits' }
  | null;

export function BillingPanel({ self }: BillingPanelProps) {
  const isAdmin = self.canManage;

  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutOutcome = searchParams.get('checkout');

  /**
   * Set while a Stripe operation is settling, which is the only time this screen polls.
   *
   * A timestamp rather than a boolean so the window can expire: an admin left on a page that polls
   * forever is a page that quietly hammers the API, and a spinner with no end is worse UX than an
   * honest "still confirming, refresh to check".
   */
  const [settlingSince, setSettlingSince] = useState<number | null>(null);
  /**
   * What the current wait is for, and what the awaited payloads looked like when it began.
   *
   * The poll used to be untargeted: every operation refetched `/billing` AND `/billing/credits` every
   * 3s for the full minute — ~40 requests — regardless of which payload could possibly carry the
   * outcome, and with nothing reading the responses to notice it had arrived. See `billingSettling.ts`.
   */
  const [settlingReason, setSettlingReason] = useState<SettlingReason | null>(null);
  /** What the awaited payloads looked like when the operation was submitted. */
  const settlingBaseline = useRef<SettlingSnapshot>({});
  const [settlingExpired, setSettlingExpired] = useState(false);
  /**
   * Latched copy of Stripe's return status.
   *
   * The URL param is stripped on arrival so a refresh cannot replay it, which means rendering
   * straight from `searchParams` shows the "nothing was charged" notice for exactly one render — long
   * enough to satisfy a code review and far too short for a person to read. What the admin needs to
   * see outlives the URL that carried it, so it is held here instead.
   */
  const [returnedFromStripe, setReturnedFromStripe] = useState<string | null>(null);

  const isSettling = settlingSince !== null && !settlingExpired;

  const settlingTargets = settlingReason ? SETTLING_TARGETS[settlingReason] : [];
  const pollFor = (target: SettlingTarget) =>
    isSettling && settlingTargets.includes(target) ? POLL_INTERVAL_MS : false;

  const billing = useOrgBilling({
    enabled: isAdmin,
    refetchInterval: pollFor('billing'),
  });
  // Polled on the same clock as billing status: the balance moves when Stripe's webhook lands, not
  // when the purchase call returns.
  const credits = useOrgCredits({
    enabled: isAdmin,
    refetchInterval: pollFor('credits'),
  });
  const creditBalance = credits.data?.balance_usd ?? 0;
  const { data: members = [] } = useOrgMembers();
  /*
   * Budget and spend come from the SAME payload the Usage and Overview tabs read — the
   * month-scoped report — not from the `/usage` rollup this used to call.
   *
   * `/usage` reports `spend_usd` = cycle spend (`live_cycle_spend/2`, the max of LiteLLM's
   * counter and our events), while every other spend figure in the panel is the selected
   * period. Measured on dev 2026-09-14: this table showed $1.5829 for the seat that Usage
   * and Overview both showed as $1.5642, with a caption underneath asserting the two came
   * from the same ledger. They did not.
   *
   * The report carries everything this screen needs (`budget_usd`, `spend_usd_window`,
   * `resets_at`, `email`), so this is one source rather than two. The settling poll moved
   * across with it — a top-up's money still lands as `budget_usd`, which this payload has.
   */
  const usageQuery = useOrgUsageReport('current', { refetchInterval: pollFor('usage') });
  const usage = usageQuery.data;

  /*
   * Fingerprints of the three awaited payloads, recomputed only when their data changes.
   *
   * Projected rather than whole-payload — see `billingSettling.ts`. The ref exists so
   * `startSettling` can capture the PRE-mutation baseline without being recreated on every data
   * change, which would make every handler that closes over it unstable.
   */
  const signatures = useMemo<SettlingSnapshot>(
    () => ({
      billing: billingSignature(billing.data),
      credits: creditsSignature(credits.data),
      usage: usageSignature(usage),
    }),
    [billing.data, credits.data, usage],
  );
  const signaturesRef = useRef<SettlingSnapshot>(signatures);
  useEffect(() => {
    signaturesRef.current = signatures;
  }, [signatures]);

  const createCheckout = useCreateCheckout();
  const changePlan = useChangePlan();
  const purchaseCredits = usePurchaseCredits();
  const assignCredits = useAssignCredits();
  const portal = useBillingPortal();
  const cancelSubscription = useCancelSubscription();
  const resumeSubscription = useResumeSubscription();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [dialog, setDialog] = useState<DialogState>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogAmbiguous, setDialogAmbiguous] = useState(false);
  /**
   * Seats whose cancellation was just scheduled through THIS panel, in-session only.
   *
   * `GET /billing`'s own `cancel_at_period_end` is now the authoritative source (see the seats
   * useMemo below) — this exists only to bridge the moment right after our cancel call succeeds:
   * that endpoint doesn't stamp the field itself, only Stripe's `customer.subscription.updated`
   * webhook does, so a refetch immediately afterward can still read `false` from the API.
   */
  const [scheduledCancellations, setScheduledCancellations] = useState<Set<string>>(new Set());
  /** The seat a mutation is running for, so only that row's actions lock. */
  const [busySeatId, setBusySeatId] = useState<string | null>(null);

  const { ref: bodyRef, width } = useContainerWidth<HTMLDivElement>();
  const isNarrow = width !== null && width < OA_NARROW_BREAKPOINT;

  /*
   * Enterprise connections do not use the plan catalogue.
   *
   * Read from the selected connection's `isEnterprise`, which already arrived in the single
   * `GetUserData` response — no extra request, and per CONNECTION rather than per user, since one
   * person can hold an enterprise production org and a self-serve sandbox.
   */
  const isEnterprise = useIsEnterpriseConnection();

  // ── Settling window ───────────────────────────────────────────────────────

  /*
   * Snapshot-then-poll. The baseline is captured from whatever is in the cache at the moment the
   * operation is submitted, so the first response that differs from it is the webhook landing.
   */
  /** Refetch exactly the payloads the current wait is watching. */
  const refetchSettlingTargets = useCallback(() => {
    if (settlingTargets.includes('billing')) void billing.refetch();
    if (settlingTargets.includes('credits')) void credits.refetch();
    if (settlingTargets.includes('usage')) void usageQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlingReason]);

  const startSettling = useCallback((reason: SettlingReason) => {
    settlingBaseline.current = signaturesRef.current;
    setSettlingReason(reason);
    setSettlingExpired(false);
    setSettlingSince(Date.now());
  }, []);

  /*
   * END THE WAIT AS SOON AS THE CHANGE IS VISIBLE.
   *
   * This is the fix for the reported symptom: the poll used to run its full 60 seconds whatever the
   * responses said, so a webhook that landed in three seconds still cost twenty refetches of two
   * payloads. Now the first response that differs from the baseline closes the window — typically
   * after one or two polls. The timeout below stays as the backstop for a webhook that never lands.
   */
  useEffect(() => {
    if (!isSettling || settlingTargets.length === 0) return;
    const { settled, baseline } = reconcileSettling(
      settlingBaseline.current,
      signatures,
      settlingTargets,
    );
    settlingBaseline.current = baseline;
    if (settled) setSettlingExpired(true);
    // `settlingTargets` is derived from `settlingReason` and rebuilt each render, so the reason is
    // the real dependency — depending on the array would run this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettling, settlingReason, signatures]);

  useEffect(() => {
    if (settlingSince === null || settlingExpired) return;
    const remaining = POLL_WINDOW_MS - (Date.now() - settlingSince);
    if (remaining <= 0) {
      setSettlingExpired(true);
      return;
    }
    const timer = setTimeout(() => setSettlingExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [settlingSince, settlingExpired]);

  /*
   * Returning from Stripe.
   *
   * `?checkout=success` means Stripe took the payment, NOT that the plan is applied — the webhook is
   * still in flight. So a success return starts the polling window rather than declaring victory.
   * The param is stripped either way so a refresh does not replay the banner.
   */
  useEffect(() => {
    if (!checkoutOutcome) return;
    setReturnedFromStripe(checkoutOutcome);
    if (checkoutOutcome === 'success') startSettling('plan');
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOutcome]);

  /*
   * Whatever the admin was told is settling, stop claiming it once the org changes — the banner
   * describes another organization's purchase, and so does the set of seats whose cancellation was
   * just scheduled.
   *
   * Keyed on the selected org, NOT on `usage.organization_id`. The query-derived key looked
   * equivalent and is not: it only changes while that request keeps succeeding, so an org switch
   * during a usage outage left `null === null` and this reset never ran. The org itself is the fact
   * this state depends on, so it is the thing to depend on.
   */
  const orgKey = self.organizationId;
  const previousOrgKey = useRef(orgKey);
  useEffect(() => {
    // Only on a CHANGE. Running on mount as well looks harmless and is not: this component mounts
    // with `?checkout=success` already in the URL on the way back from Stripe, the effect above sets
    // the settling window during that same commit, and this one — declared later, so it runs
    // second — cleared it again. The banner was unreachable by the one route that always produces it.
    if (previousOrgKey.current === orgKey) return;
    previousOrgKey.current = orgKey;
    setSettlingSince(null);
    setSettlingExpired(false);
    setSettlingReason(null);
    setReturnedFromStripe(null);
    setScheduledCancellations(new Set());
    setDialog(null);
    setDialogError(null);
    setDialogAmbiguous(false);
    setBusySeatId(null);
  }, [orgKey]);

  // ── Joined seats ──────────────────────────────────────────────────────────

  /**
   * One row per seat the billing API reports.
   *
   * Billing is the authority on who has a billable seat, so it drives the list; the members list and
   * the usage rollup only enrich it. Driving from `members` instead would invent rows the billing API
   * knows nothing about.
   */
  const seats = useMemo<BillingSeat[]>(() => {
    if (!billing.data) return [];
    const emailByUserId = new Map(members.map((member) => [member.user_id, member.email]));
    const usageByUserId = new Map((usage?.members ?? []).map((row) => [row.user_id, row]));

    return billing.data.members
      .map((row) => {
        const usageRow = usageByUserId.get(row.user_id);
        return {
          userId: row.user_id,
          email: emailByUserId.get(row.user_id) ?? usageRow?.email ?? row.user_id,
          // Both straight from the billing row. `plan` and `budgetUsd` are independent facts now —
          // nothing here derives one from the other.
          plan: row.plan,
          billingMode: row.billing_mode,
          subscriptionStatus: row.subscription_status,
          planCycleEndsAt: row.plan_cycle_ends_at,
          budgetUsd: usageRow?.budget_usd ?? null,
          // `spend_usd_window`, not `spend_usd_cycle` — the figure every other spend tile
          // in the admin panel shows. See the query's own comment above.
          spendUsd: usageRow?.spend_usd_window ?? null,
          // The plan/credits split and what is left THIS CYCLE, from the same report row (#327).
          allowance: usageRow ? memberAllowance(usageRow) : null,
          // The API's own flag first; the in-session set only covers the brief post-cancel window
          // before Stripe's webhook stamps it (see the state's own doc comment above).
          cancellationScheduled: row.cancel_at_period_end || scheduledCancellations.has(row.user_id),
          endedPlan: row.ended_plan ?? null,
          planEndedAt: row.plan_ended_at ?? null,
        };
      })
      // Paid and ended seats first, then by spend: the rows that cost money, or need buying for,
      // are the ones worth reading.
      .sort((a, b) => {
        const paid = (seat: BillingSeat) => (seat.billingMode === null && !hasEnded(seat) ? 1 : 0);
        if (paid(a) !== paid(b)) return paid(a) - paid(b);
        return (b.spendUsd ?? 0) - (a.spendUsd ?? 0);
      });
  }, [billing.data, members, usage, scheduledCancellations]);

  /*
   * Whether credit may be bought, and why not when it may not.
   *
   * Credit tops up a PLAN'S budget, so it needs a seat holding a live catalogue subscription — see
   * `creditEligibility.ts`. Deliberately not an `isEnterprise` check: an enterprise connection holds
   * no catalogue plan, so it fails this same rule without a special case, and an enterprise
   * connection that DOES carry live subscriptions keeps working as it does today.
   */
  const creditBlockedReason = creditPurchaseBlockedReason(seats, {
    canManage: self.canManage,
    isLoading: billing.isLoading,
  });

  /**
   * How the org's seats distribute across the catalogue.
   *
   * `null` until billing has loaded, which is what lets the matrix reserve the row rather than grow
   * one when the counts arrive. Derived from the seats already joined above — no extra request, no
   * second copy of the data, and one shared derivation with the plan shown on each row.
   */
  const planTally = useMemo(
    () => (billing.data ? tallySeatPlans(seats) : null),
    [billing.data, seats],
  );

  /**
   * Plans there is nobody left to buy for.
   *
   * A plan belongs to a seat, so "somebody is on Growth" must NOT disable Growth — another member may
   * still want it. It is only a dead end when every active seat already holds it under a LIVE
   * subscription, which is the one case the backend refuses with a 409. In a single-member org that
   * condition is simply "this is my current plan", which is what the matrix then says.
   */
  const atCapacity = useMemo<PurchasablePlan[]>(
    () =>
      billing.data
        ? plansAtCapacity(
            seats.map((seat) => ({ plan: seat.plan, liveSubscription: hasLiveSubscription(seat) })),
          )
        : [],
    [billing.data, seats],
  );

  const paidSeats = seats.filter((seat) => seat.billingMode !== null);
  const attentionSeats = seats.filter(
    (seat) => seat.subscriptionStatus === 'past_due' || seat.subscriptionStatus === 'unpaid',
  );
  const endingSeats = seats.filter((seat) => hasLiveSubscription(seat) && seat.cancellationScheduled);
  const endedSeats = seats.filter(hasEnded);
  /*
   * What an ended plan means for the member depends on credit (#327): credit survives the ending,
   * so a member holding some is slowed, not stopped. One sentence that is true for the whole group,
   * rather than "no AI credits" said about someone who has $7.50 of them.
   */
  const endedWithCredit = endedSeats.filter((seat) => (creditLeftAfterEnd(seat) ?? 0) > 0);
  const endedSeatsConsequence =
    endedWithCredit.length === 0
      ? `${endedSeats.length === 1 ? 'This member has' : 'These members have'} no allowance and can’t use AI features until you buy a plan.`
      : endedWithCredit.length === endedSeats.length
        ? `${endedSeats.length === 1 ? 'This member' : 'They'} can still spend the credit ${endedSeats.length === 1 ? 'they hold' : 'each holds'}, and nothing else, until you buy a plan.`
        : `${endedWithCredit.map((seat) => seat.email).join(', ')} can still spend the credit they hold; the rest have no allowance until you buy a plan.`;

  const stats: StatSpec[] = billing.data
    ? [
                {
          label: 'Paid seats',
          // "active" is load-bearing: the billing API omits deactivated members, so this count and
          // the Members tab's badge legitimately differ.
          value: String(paidSeats.length),
          hint: `of ${seats.length} active members`,
        },
        {
          label: 'Monthly',
          value: String(paidSeats.filter((seat) => seat.billingMode === 'recurring').length),
          hint: 'recurring subscriptions',
        },
        // {
        //   label: 'Needs attention',
        //   value: String(attentionSeats.length),
        //   hint: 'past due or unpaid',
        //   tone: attentionSeats.length > 0 ? 'warn' : 'default',
        // },
      ]
    : [];

  // ── Actions ───────────────────────────────────────────────────────────────

  /** Full-page redirect, matching how the app already leaves for Salesforce and Slack OAuth. */
  const leaveTo = (url: string) => {
    window.location.href = url;
  };

  /*
   * Every handler below awaits a request and then changes the screen. The org can change during that
   * await, and `useBilling`'s token guard only protects the query CACHE — it says nothing about a
   * banner, a toast, or a redirect to Stripe.
   *
   * Left unguarded, org A's top-up resolving after a switch raised "Payment received" over org B and
   * started polling org B's billing, and an org A checkout resolving late would have thrown the
   * browser to a Stripe page for a seat in an organization the admin had already left. So each
   * handler captures the org it was started for and checks it before it is allowed to act.
   *
   * The epoch inside the token is what makes A → B → A safe: same id, later epoch, still stale.
   */
  const handleBuyPlan = async (plan: PurchasablePlan, pickedUserId?: string) => {
    if (dialog?.kind !== 'plan') return;
    // The row entry point fixed the seat; the catalogue entry point sends back the picked one. Both
    // resolve here so exactly one id reaches the request — a checkout addressed to the wrong member
    // charges a real card and cannot be undone from this screen.
    const userId = dialog.seat?.userId ?? pickedUserId;
    if (!userId) return;
    const token = captureOrgToken();
    setDialogError(null);
    setBusySeatId(userId);

    /*
     * Two different transactions behind one dialog.
     *
     * A seat with a live subscription is REPRICED — the backend refuses a second Checkout for it
     * (409 `subscription_already_active`), because that would create a second Stripe Subscription
     * billing alongside the first. Everyone else goes through Checkout as before.
     *
     * Note the row already offered "Change plan for …" before this existed, and it called the
     * checkout path — so that button reliably produced that 409. This is what makes it work.
     *
     * `dialog.seat` is only set for the row entry point — the catalogue entry point resolves the
     * seat at submit time via `pickedUserId`, so it has to be looked up here too, or a member with
     * a live subscription picked from the catalogue would always fall through to Checkout and hit
     * the same 409.
     */
    const targetSeat = dialog.seat ?? seats.find((seat) => seat.userId === userId) ?? null;
    const changingSeat = targetSeat && hasLiveSubscription(targetSeat) ? targetSeat : null;

    try {
      if (changingSeat) {
        await changePlan.mutateAsync({ user_id: userId, plan });
        if (!isOrgTokenCurrent(token)) return;
        // No redirect: the change is already applied server-side, unlike a purchase which only
        // completes once Stripe's webhook lands.
        setDialog(null);
        setBusySeatId(null);
        return;
      }

      const { checkout_url } = await createCheckout.mutateAsync({
        user_id: userId,
        plan,
      });
      if (!isOrgTokenCurrent(token)) return;
      leaveTo(checkout_url);
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      setDialogError(billingErrorMessage(error, changingSeat ? 'changePlan' : 'checkout'));
      setBusySeatId(null);
    }
  };

  const handleBuyCredits = async (amountUsd: number) => {
    const token = captureOrgToken();
    setDialogError(null);
    setDialogAmbiguous(false);
    try {
      const result = await purchaseCredits.mutateAsync({ amount_usd: amountUsd });
      if (!isOrgTokenCurrent(token)) return;
      if ('checkout_url' in result) {
        leaveTo(result.checkout_url);
        return;
      }
      setDialog(null);
      // A credit purchase lands in the org balance, so that is the only payload worth polling.
      startSettling('credit');
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      setDialogAmbiguous(isAmbiguousFailure(error));
      setDialogError(billingErrorMessage(error, 'topup'));
    }
  };

  const handleAssignCredits = async (
    allocations: Array<{ user_id: string; amount_usd: number }>,
  ) => {
    const token = captureOrgToken();
    setDialogError(null);
    setDialogAmbiguous(false);
    try {
      const result = await assignCredits.mutateAsync({ allocations });
      if (!isOrgTokenCurrent(token)) return;
      /*
       * Closed immediately, and NOT put into the settling poll. Unlike every other action on this
       * screen, assignment takes no money and involves no webhook — the budgets have already moved
       * by the time this resolves, so polling would be waiting for something that already happened.
       */
      setDialog(null);
      /*
       * A 200 is not a clean success when `failed_user_ids` is non-empty: the pool was debited for
       * those seats and their allowance did not move (#327). That is money in limbo, so it is said
       * by name rather than folded into a success toast.
       */
      const failed = result.failed_user_ids ?? [];
      if (failed.length > 0) {
        const emailOf = new Map(seats.map((seat) => [seat.userId, seat.email]));
        const names = failed.map((id) => emailOf.get(id) ?? id).join(', ');
        toast.error(
          `Credit was taken from the pool but did not reach ${names}. Refresh and check their allowance before assigning again.`,
          { duration: 10_000 },
        );
      } else {
        toast.success('Credit assigned — it stays with each member until spent, across resets.');
      }
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      // No ambiguity branch: nothing was charged, so a failed assignment is simply retryable.
      setDialogError(billingErrorMessage(error, 'topup'));
    }
  };

  const handlePortal = async () => {
    const token = captureOrgToken();
    try {
      const { portal_url } = await portal.mutateAsync();
      if (!isOrgTokenCurrent(token)) return;
      leaveTo(portal_url);
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      toast.error(billingErrorMessage(error, 'portal'));
    }
  };

  const handleCancel = async (seat: BillingSeat) => {
    const token = captureOrgToken();
    /*
     * Names the date when the API gives us one.
     *
     * `plan_cycle_ends_at` is the renewal this cancellation prevents, so it is exactly the day the
     * seat drops to free. Until the field existed this dialog could only say "the end of the current
     * billing period", and an admin deciding whether to cancel had no way to tell whether that meant
     * tomorrow or in three weeks. Falls back to the vaguer wording rather than inventing a date.
     */
    // Absolute ("Oct 3, 2026"), not relative: a sentence read once while deciding is easier to
    // check against a calendar.
    const endsOn = seat.planCycleEndsAt ? (formatCycleEnd(seat.planCycleEndsAt)?.title ?? null) : null;

    const confirmed = await confirmModal.error({
      title: 'Cancel this subscription?',
      content: `${seat.email} keeps their paid budget until ${endsOn ?? 'the end of the current billing period'}, then moves back to the free plan. This does not take effect immediately and nothing is refunded.`,
      okText: 'Schedule cancellation',
      cancelText: 'Keep it',
      okButtonProps: { variant: 'destructive' },
    });
    // The confirmation itself is an await, so the org can have changed before the admin answered.
    if (!confirmed || !isOrgTokenCurrent(token)) return;

    setBusySeatId(seat.userId);
    try {
      await cancelSubscription.mutateAsync(seat.userId);
      if (!isOrgTokenCurrent(token)) return;
      // NOT "cancelled": Stripe still reports the subscription as active until the period ends.
      setScheduledCancellations((current) => new Set(current).add(seat.userId));
      toast.success('Cancellation scheduled for the end of the current period');
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      toast.error(billingErrorMessage(error, 'cancel'));
    } finally {
      if (isOrgTokenCurrent(token)) setBusySeatId(null);
    }
  };

  const handleResume = async (seat: BillingSeat) => {
    const token = captureOrgToken();
    setBusySeatId(seat.userId);
    try {
      await resumeSubscription.mutateAsync(seat.userId);
      if (!isOrgTokenCurrent(token)) return;
      // The in-session flag would otherwise keep reporting the cancellation the backend just undid.
      setScheduledCancellations((current) => {
        const next = new Set(current);
        next.delete(seat.userId);
        return next;
      });
      toast.success(`${seat.email}’s subscription will renew`);
    } catch (error) {
      if (!isOrgTokenCurrent(token)) return;
      toast.error(billingErrorMessage(error, 'resume'));
    } finally {
      if (isOrgTokenCurrent(token)) setBusySeatId(null);
    }
  };

  const rowHandlers = {
    canManage: self.canManage,
    onBuyPlan: (target: BillingSeat) => setDialog({ kind: 'plan', seat: target }),
    onCancel: handleCancel,
    onResume: handleResume,
  };

  // ── Non-admin ─────────────────────────────────────────────────────────────

  if (!isAdmin) {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        <OrgAdminEmptyState
          icon={Lock}
          title="Billing is available to owners and admins"
          description="Plans, payment methods and subscriptions need admin authority. Ask an owner if you need access."
        />
      </div>
    );
  }

  if (billing.isError) {
    return (
      <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
        {/* The mapped message, not the raw axios error: a 403 here means "you are not an admin",
            which is worth saying instead of surfacing a status line. */}
        <OrgAdminErrorState
          title="Couldn't load billing"
          error={new Error(billingErrorMessage(billing.error, 'portal'))}
          onRetry={() => void billing.refetch()}
        />
      </div>
    );
  }

  const hasPaymentMethod = billing.data?.has_payment_method ?? false;

  /*
   * The saved card, named.
   *
   * `payment_method` is null both when there is no card and when there is one whose details could not
   * be read — the backend collapses those two, so the UI does too and falls back to the copy it used
   * before the field existed. `has_payment_method` stays the authority on whether a card exists.
   *
   * Nothing here is logged, stored or forwarded: brand, last four and expiry are rendered and
   * discarded. That is the whole of what the frontend is told about the card, and all of it is
   * displayable — there is no full number and no token to leak.
   */
  const paymentMethod = billing.data?.payment_method ?? null;
  const cardExpired = paymentMethod ? isCardExpired(paymentMethod) : false;
  const cardTitle = paymentMethod
    ? `${cardBrandLabel(paymentMethod.brand)} •••• ${paymentMethod.last4}`
    : null;
  const cardDetail = paymentMethod
    ? cardExpired
      ? `This card expired ${formatCardExpiry(paymentMethod)}. Update it in the payment portal — charges against it will fail.`
      : `Expires ${formatCardExpiry(paymentMethod)} — cards and invoices are managed in Stripe’s secure portal.`
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
        <p className="oa-meta min-w-0 flex-1 basis-52 truncate">
          {/*
            THE SCOPE SENTENCE, and it is load-bearing.

            Billing is per CONNECTION — one connected Salesforce environment — not per company. A
            customer with Production plus two sandboxes has three separate Stripe scopes, three
            credit balances and three sets of invoices, and there is no company-level entity anywhere
            in the product to roll them up. Without this sentence an admin can reasonably read this
            screen as "what my company pays for nCoder", buy accordingly, and be wrong. Saying it
            here is cheap; a frontend that summed across connections would be inventing a total.
          */}
          Plans, payment method and subscriptions — for this connection only.
        </p>
      </div>

      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 p-[var(--oa-gutter)]">
          {/* ── Settling banner ─────────────────────────────────────────────
              The honest middle state. Stripe has the money; our backend does not yet know. */}
          {settlingSince !== null && (
            <div
              role="status"
              className={cn(
                'flex items-start gap-2.5 rounded-[var(--oa-radius-md)] border p-3',
                settlingExpired
                  ? 'border-warning/30 bg-warning/[0.07]'
                  : 'border-primary/25 bg-primary/[0.05]',
              )}
            >
              {settlingExpired ? (
                <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-warning-strong" aria-hidden />
              ) : (
                <Loader2 className="mt-px h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold">
                  {settlingExpired ? 'Still confirming with Stripe' : 'Payment received — applying it'}
                </p>
                <p className="oa-meta mt-0.5">
                  {settlingExpired
                    ? 'This is taking longer than usual. Your payment is safe; the plan or budget will appear once Stripe confirms it.'
                    : 'Stripe confirms payments in the background, so the plan and budget below update a moment after paying.'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 text-[12px]"
                onClick={() => {
                  /*
                   * Re-arm the SAME wait rather than starting a generic one: the banner is showing
                   * because a specific operation has not landed yet, and "Refresh" means "look
                   * again for that". Falls back to the plan payload if the reason was somehow lost,
                   * which is the pre-existing behaviour and the widest of the three.
                   */
                  void refetchSettlingTargets();
                  startSettling(settlingReason ?? 'plan');
                }}
              >
                <RefreshCw className="h-3 w-3" aria-hidden />
                Refresh
              </Button>
            </div>
          )}

          {returnedFromStripe === 'cancelled' && (
            <p role="status" className="oa-meta px-1">
              Checkout was cancelled. Nothing was charged.
            </p>
          )}

          {endedSeats.length > 0 && (
            <BillingNotice
              tone="bad"
              icon={AlertTriangle}
              label="Ended subscriptions"
              title={
                endedSeats.length === 1
                  ? '1 subscription has ended'
                  : `${endedSeats.length} subscriptions have ended`
              }
              action={
                <Button
                  type="button"
                  size="sm"
                  className="h-7 shrink-0 text-[12px]"
                  disabled={!self.canManage}
                  title={self.canManage ? undefined : NO_PERMISSION_REASON}
                  // One seat: buy for it directly. Several: the catalogue picker, to choose which.
                  onClick={() =>
                    setDialog({ kind: 'plan', seat: endedSeats.length === 1 ? endedSeats[0] : null })
                  }
                >
                  Buy a plan
                </Button>
              }
            >
              Ended {seatDates(endedSeats, (seat) => seat.planEndedAt)}. {endedSeatsConsequence}
            </BillingNotice>
          )}

          {endingSeats.length > 0 && (
            <BillingNotice
              tone="warn"
              icon={Clock}
              label="Ending subscriptions"
              title={
                endingSeats.length === 1
                  ? '1 subscription is ending'
                  : `${endingSeats.length} subscriptions are ending`
              }
            >
              Cancelled and won’t renew: {seatDates(endingSeats, (seat) => seat.planCycleEndsAt)}.
              Renew a subscription to keep that member’s plan and budget.
            </BillingNotice>
          )}

          {attentionSeats.length > 0 && (
            <BillingNotice
              tone="bad"
              icon={AlertTriangle}
              label="Payment problems"
              title={
                attentionSeats.length === 1
                  ? '1 subscription needs attention'
                  : `${attentionSeats.length} subscriptions need attention`
              }
            >
              Stripe could not take payment. It will retry automatically; update the card in the
              payment portal to resolve it sooner.
            </BillingNotice>
          )}

          {/* ── Where you stand ─────────────────────────────────────────────
              PROMOTED ABOVE THE CATALOGUE, which is the one substantive reordering on this screen.

              The plan matrix used to render here and the summary of what the org already has came
              third, below it — so Billing answered "what could I buy?" before "where do I stand?".
              That is the right order for exactly one visit, the first; on every visit after it the
              admin arrives knowing the catalogue and wanting the current state. The catalogue is
              still immediately below, so nothing is harder to find.

              Both blocks sit under one heading because they are one answer: what we are paying for,
              and the card it is charged to. */}
          <Section title="What are we paying for?">
          <OrgUsageStatRail
            stats={stats}
            isLoading={billing.isLoading}
            skeletonCount={4}
            ariaLabel="Billing summary"
          />

          {/* ── Payment method ──────────────────────────────────────────────
              First of the payment-management group, because every action in it depends on a card. */}
          {!billing.isLoading && (
            <section
              className={cn(
                'flex flex-wrap mt-2 items-center justify-between gap-x-4 gap-y-2 rounded-[var(--oa-radius-md)] border p-3',
                // An expired card is treated as a problem, not as a card. It IS on file, so the
                // old boolean painted this calm and green while every future charge against it fails.
                hasPaymentMethod && !cardExpired
                  ? 'border-border/60 bg-muted/20'
                  : 'border-warning/30 bg-warning/[0.07]',
              )}
              aria-label="Payment method"
            >
              <div className="flex min-w-0 items-start gap-2.5">
                {hasPaymentMethod && !cardExpired ? (
                  <CheckCircle2
                    className="mt-px h-4 w-4 shrink-0 text-success-strong"
                    aria-hidden
                  />
                ) : (
                  <CreditCard className="mt-px h-4 w-4 shrink-0 text-warning-strong" aria-hidden />
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-medium">
                    {hasPaymentMethod
                      ? (cardTitle ?? 'Payment method on file')
                      : 'No payment method on file'}
                  </p>
                  <p className="oa-meta mt-0.5">
                    {hasPaymentMethod
                      ? (cardDetail ?? 'Cards and invoices are managed in Stripe’s secure portal.')
                      : 'You can still buy a plan — Stripe saves the card during checkout.'}
                  </p>
                </div>
              </div>
              {/* An expired card needs the same route to the portal that a missing one gets. */}
              {(!hasPaymentMethod || cardExpired) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0"
                  onClick={handlePortal}
                  disabled={portal.isPending}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  {cardExpired ? 'Update card' : 'Add a card'}
                </Button>
              )}
            </section>
          )}

          </Section>

          {/*
            THE PLAN CATALOGUE IS HIDDEN FOR AN ENTERPRISE CONNECTION.

            Not disabled — hidden. A disabled price table invites the admin to ask how to enable it,
            and the answer is "you cannot, and you do not want to": enterprise billing is a contract
            negotiated outside this catalogue, and `BillingPlan` has no enterprise tier to show. A
            self-serve purchase alongside that contract would be a second, conflicting subscription.

            Everything else on this screen stays: payment method, credit, the per-seat table and
            invoices are all still meaningful for an enterprise connection, and hiding them would be
            removing function the brief did not ask to remove.
          */}
          {!isEnterprise && (
          <Section title="What could we change to?">
          {/* ── Plans ───────────────────────────────────────────────────────
              Directly below "where you stand", because it is the answer to the follow-up question:
              given what we have, what could we change it to. It sits BELOW the settling and past-due
              banners for the same reason it always did — a static price table must never come between
              an admin and the news that a payment failed.

              Nothing here loads. The catalogue is a module constant mirroring fixed server config, so
              this section renders identically on first paint and cannot shift the page when billing
              resolves. Only its CTA depends on the seat list. */}
          <PlanMatrix
            isNarrow={isNarrow}
            canManage={self.canManage}
            isLoadingSeats={billing.isLoading}
            tally={planTally}
            plansAtCapacity={atCapacity}
            disabledReason={NO_PERMISSION_REASON}
            onChoosePlan={(plan) => {
              setDialogError(null);
              setDialog({ kind: 'plan', seat: null, plan });
            }}
          />
          </Section>
          )}

          <Section title="Who has what?">
          {/* ── Credit pool ─────────────────────────────────────────────────
              Above the seats, because buying is the step that has to happen before assigning and
              this is where the balance that funds the seat table below actually lives. */}
          <section className="oa-panel" aria-labelledby="oa-credits-title">
            <header className="oa-toolbar flex flex-wrap items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <h3 id="oa-credits-title" className="oa-title">
                  Credit
                </h3>
                <p className="oa-meta mt-0.5">
                  Add credit to the organization, then assign it to members whenever you like.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!creditBlockedReason}
                  title={creditBlockedReason}
                  onClick={() => {
                    setDialogError(null);
                    setDialogAmbiguous(false);
                    setDialog({ kind: 'buy-credits' });
                  }}
                >
                  <Wallet className="h-3.5 w-3.5" aria-hidden />
                  Add credit
                </Button>
                {/*
                  Disabled with an empty balance rather than hidden: the button is how an admin
                  learns assignment exists, and hiding it on a fresh org means they never find out
                  the pool is a two-step flow.
                */}
                <Button
                  type="button"
                  size="sm"
                  disabled={!self.canManage || creditBalance <= 0 || seats.length === 0}
                  title={
                    !self.canManage
                      ? NO_PERMISSION_REASON
                      : creditBalance <= 0
                        ? 'Add credit before assigning it'
                        : undefined
                  }
                  onClick={() => {
                    setDialogError(null);
                    setDialog({ kind: 'assign-credits' });
                  }}
                >
                  Assign
                </Button>
              </div>
            </header>

            <div className="p-[var(--oa-gutter)] pb-0">
              <p className="oa-micro">Available to assign</p>
              <p className="mt-1 text-[26px] font-semibold leading-none tabular-nums text-foreground">
                {credits.isLoading ? '—' : formatCurrencyUsd(creditBalance)}
              </p>
            </div>

            {/* Full history rather than just the last movement — a balance with no trail is a
                number an admin has to take on trust, and "last movement" can't answer where the
                rest of it came from or went once there is more than one entry. */}
            <div className="mt-3">
              <CreditLedgerTable entries={credits.data?.entries} isLoading={credits.isLoading} />
            </div>
          </section>

          {/* ── Seats ───────────────────────────────────────────────────────── */}
          <section className="oa-panel" aria-label="Member billing">
            <header className="oa-toolbar flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <h3 className="oa-title">Members</h3>
                <p className="oa-meta mt-0.5 truncate">
                  Buy a plan for any active member.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {billing.isFetching && !billing.isLoading && (
                  <span className="oa-meta flex shrink-0 items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    Refreshing
                  </span>
                )}
              </div>
            </header>

            <div className={cn(billing.isFetching && !billing.isLoading && 'opacity-60')}>
              {billing.isLoading ? (
                <OrgAdminTableSkeleton
                  rows={5}
                  columns={['34%', '90px', '110px', '90px']}
                  label="Loading billing…"
                />
              ) : seats.length === 0 ? (
                <OrgAdminEmptyState
                  icon={Users}
                  title="No active members to bill"
                  description="Members appear here once they accept an invitation. Deactivated members are not shown, even if they still have a subscription."
                />
              ) : isNarrow ? (
                <div>
                  {seats.map((seat) => (
                    <BillingMemberCard
                      key={seat.userId}
                      seat={seat}
                      isBusy={busySeatId === seat.userId}
                      {...rowHandlers}
                    />
                  ))}
                </div>
              ) : (
                <table className="oa-table">
                  <caption className="sr-only">
                    Active members, their plan, subscription status, allowance split between plan
                    and credits, and what each can still spend this cycle
                  </caption>
                  <thead>
                    <tr>
                      <PlainHeader label="Member" />
                      <PlainHeader label="Plan" width="132px" />
                      <PlainHeader label="Subscription" width="150px" />
                      <PlainHeader label="Allowance" align="right" width="130px" />
                      <PlainHeader label="Available · cycle" align="right" width="140px" />
                      <PlainHeader label="Actions" align="right" width="104px" srOnly />
                    </tr>
                  </thead>
                  <tbody>
                    {seats.map((seat) => (
                      <BillingMemberRow
                        key={seat.userId}
                        seat={seat}
                        isBusy={busySeatId === seat.userId}
                        {...rowHandlers}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {!billing.isLoading && seats.length > 0 && (
              <div className="oa-footer-rail flex flex-wrap items-center justify-between gap-2 py-1.5">
                <p className="oa-meta">
                  Allowance and available are each member&rsquo;s own cycle — the clock their limit is
                  enforced on. Credit assigned from the pool carries over between cycles; the plan part
                  resets at renewal.
                </p>
                {!self.canManage && <p className="oa-meta">{NO_PERMISSION_REASON}</p>}
              </div>
            )}
          </section>

          </Section>

          {/*
            Last on the page, and deliberately so: invoices are the RECORD of what the sections
            above did. An admin arrives here to change something — buy, top up, swap a plan — and
            reads the receipt afterwards, so the ledger sits after the controls rather than
            competing with them for the top of the screen.
          */}
          <InvoicesPanel enabled={self.canManage} />
        </div>
      </div>

      <PlanPurchaseDialog
        open={dialog?.kind === 'plan'}
        memberLabel={dialog?.kind === 'plan' ? (dialog.seat?.email ?? '') : ''}
        // Supplied only for the catalogue entry point, which is what switches the picker on. Buying
        // from a member's row keeps the seat fixed and the dialog unchanged.
        seatOptions={
          dialog?.kind === 'plan' && dialog.seat === null
            ? seats.map((seat) => ({
                userId: seat.userId,
                email: seat.email,
                // Only a LIVE subscription makes re-buying the same plan a 409 — a lapsed or
                // cancelled seat may buy again. Same predicate the row entry point gates on.
                subscribedPlan:
                  hasLiveSubscription(seat) && seat.plan !== 'free' ? seat.plan : null,
              }))
            : undefined
        }
        initialPlan={dialog?.kind === 'plan' ? dialog.plan : undefined}
        /*
         * Presence of this prop is what switches the dialog into change mode, so it is set only for
         * a seat that genuinely has a live subscription to reprice — not merely one holding a
         * non-free plan. A cancelled seat still holds its plan but has no subscription; offering it
         * a "change" would produce a backend rejection the admin cannot act on.
         */
        currentPlan={
          dialog?.kind === 'plan' && dialog.seat && hasLiveSubscription(dialog.seat)
            ? (dialog.seat.plan as PurchasablePlan)
            : undefined
        }
        isSubmitting={createCheckout.isPending || changePlan.isPending}
        errorMessage={dialogError}
        onSubmit={handleBuyPlan}
        onClose={() => {
          setDialog(null);
          setDialogError(null);
        }}
      />

      <BuyCreditsDialog
        open={dialog?.kind === 'buy-credits'}
        hasPaymentMethod={hasPaymentMethod}
        bonusMultiplier={billing.data?.topup_bonus_multiplier}
        currentBalanceUsd={creditBalance}
        isSubmitting={purchaseCredits.isPending}
        errorMessage={dialogError}
        isAmbiguous={dialogAmbiguous}
        onSubmit={handleBuyCredits}
        onClose={() => {
          setDialog(null);
          setDialogError(null);
          setDialogAmbiguous(false);
        }}
      />

      {/* The same dialog as the bulk charge, in `assign` mode — picking members and amounts is one
          task, and only the funding source differs. */}
      <BulkTopUpDialog
        open={dialog?.kind === 'assign-credits'}
        mode="assign"
        availableUsd={creditBalance}
        seats={seats.map((seat) => ({
          userId: seat.userId,
          email: seat.email,
          // So each selected row can preview the member's new available figure (#327 split).
          memberAvailableUsd: seat.allowance?.remainingUsd ?? null,
        }))}
        hasPaymentMethod={hasPaymentMethod}
        bonusMultiplier={billing.data?.topup_bonus_multiplier}
        isSubmitting={assignCredits.isPending}
        errorMessage={dialogError}
        isAmbiguous={false}
        onSubmit={handleAssignCredits}
        onClose={() => {
          setDialog(null);
          setDialogError(null);
        }}
      />

      {confirmModalContext}
    </div>
  );
}

export { hasLiveSubscription };
